import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import './LobbyListPage.css';
import PageButton from '../elements/PageButton/PageButton';
import {
  clearExpectingClose,
  isExpectingClose,
  markExpectingClose,
  triggerConnectionLost
} from '../../utils/connectionMonitor';

import {API_BASE, WS_BASE} from '../../utils/apiConfig';
import {usePageAnalytics} from '../../hooks/analytics/usePageAnalytics';
import {PlayerIcon} from '../coloredIcons/PlayerIcon/PlayerIcon';
import PrivateLobbyOverlay from '../PrivateLobbyOverlay/PrivateLobbyOverlay';
import FloatingBackButton from '../elements/FloatingBackButton/FloatingBackButton';
import LoginRequiredModal from '../Modals/LoginRequiredModal/LoginRequiredModal';
import {LOGIN_REQUIRED_REASONS} from '../../configs/guestProfile';
import {
  RANK_MATCH_JOIN_NOT_ALLOWED_MESSAGE,
  RANK_MATCH_PHASE_STARTED_MESSAGE,
  resolveInvitedLobbyJoinOutcome,
} from '../../utils/lobby/resolveInvitedLobbyJoinOutcome';
import {
  checkIfGuestCanSpectateLobby,
  checkIfGuestJoinRefused,
  getLobbyNetworkIdentity,
  GUEST_SPECTATE_UNAVAILABLE_MESSAGE,
} from '../../utils/lobby/guestViewer';
import {sendPageEvent} from '../../analytics/gameAnalytics';
import {AI_MATCH_SHORT_TITLE} from '../../configs/aiMatch';
import {getAiLobbyJoinErrorText} from '../../utils/aiMatch/getAiLobbyJoinErrorText';
import {useDesktopLayout} from '../../hooks/visuals/useWideScreenLayout';
import {getLobbyItemButtonId, sortAiLobbiesFirst} from './lobbyListItems';
import LobbyListTypeIcons from './LobbyListTypeIcons';
import LobbyListDesktop from './desktop/LobbyListDesktop';

// id плашек и порядок ИИ-лобби живут рядом (их берут и карточки десктопа), а
// отсюда — по-прежнему
export {getLobbyItemButtonId, LOBBY_LIST_ITEM_BUTTON_IDS, sortAiLobbiesFirst} from './lobbyListItems';

// Отказ сервера: игрок уже состоит в другом лобби
const ALREADY_IN_ANOTHER_LOBBY_ERROR = 'Player is already in another lobby';

// Итог попытки гостя войти зрителем — отдельным событием, клик по плашке сам
// по себе не говорит, пустил ли сервер: page:LobbyListPage:guest_spectate:ok
// или :refused:<not_found|forbidden|error>
const getGuestSpectateRefusalLevel = (status) => {
  if (status === 404) return 'not_found';
  if (status === 403) return 'forbidden';
  return 'error';
};

// Пояснение под заголовком — одно на обе раскладки
const LOBBY_LIST_INFO_TEXT = (
  <>
    В обычных матчах можно позвать друзей по ссылке и сыграть вместе или помериться силами с другими
    игроками. Награды за них скромнее, чем за <span className="text-attention">рейтинговые матчи</span>.
  </>
);

function LobbyListPage({
                         onEnterLobby, onBack, onNotEnoughFactions, onLobbyIsFull, onShop,
                         initialError, onInitialErrorShown, onDevLogin, onEmailAuthSuccess,
                         onRankedMatch,
                       }) {
  const [lobbies, setLobbies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(initialError || null);
  const [showPrivateLobbyOverlay, setShowPrivateLobbyOverlay] = useState(false);
  // Гость смотрит список и может войти зрителем в начатый матч, но войти в
  // лобби в сборе или создать свое без аккаунта не может — вместо этого
  // модалка входа (как у кнопок главного меню)
  const [loginRequiredReason, setLoginRequiredReason] = useState(null);
  const auth = useSelector((state) => state.private.auth);
  const isGuest = Boolean(auth.isGuest);
  const isAdmin = auth.isAdmin;
  // Скрытые признаки ботов (цвет звезды рейтинга) сервер отдает и админу, и
  // суперадмину — так же и здесь
  const isViewerAdmin = Boolean(auth.isAdmin || auth.isSuperAdmin);
  // Под каким id и именем ходить в сеть: у гостя — id вкладки (см.
  // utils/lobby/guestViewer). Без него все гости подписывались бы на список
  // под строкой «null», и обновления получал бы только последний
  const {id: networkId, name: networkName} = getLobbyNetworkIdentity(auth);
  const wsRef = useRef(null);
  const mountedRef = useRef(true);
  // От 1024 px — своя раскладка (LobbyListDesktop): карточки лобби и нижняя
  // полоса кнопок, как у десктопного лобби
  const isDesktopLayout = useDesktopLayout();

  usePageAnalytics('LobbyListPage');

  // Сообщение, с которым экран открыт снаружи (несостоявшийся вход по
  // приглашению). Через эффект, а не только через инициализатор useState:
  // экран может быть уже смонтирован, когда сообщение появилось (например,
  // join по приглашению отвечал дольше, чем игрок дошел до списка сам).
  // Гасим в источнике сразу после показа — иначе всплывет при следующем
  // открытии списка
  useEffect(() => {
    if (!initialError) return;
    setError(initialError);
    onInitialErrorShown?.();
  }, [initialError, onInitialErrorShown]);

  const fetchLobbies = async (signal) => {
    try {
      // С кукой входа: на пилоте промо-лобби с ИИ сервер показывает только
      // админам (AI_PROMO_VISIBILITY=admins). На проде кука и так уходит (тот
      // же источник), а на дев-сервере (другой порт) — только с include
      const res = await fetch(`${API_BASE}/lobbies`, {signal, credentials: 'include'});
      if (!res.ok) throw new Error('Failed to fetch lobbies');
      const data = await res.json();
      if (!mountedRef.current) return;
      setLobbies(data);
    } catch (e) {
      if (!mountedRef.current) return;
      if (e.name === 'AbortError') return;
      setError(e.message);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();
    fetchLobbies(controller.signal);

    const wsUrl = `${WS_BASE}?type=lobbyList&playerId=${encodeURIComponent(networkId)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[LobbyListPage] WS connected');
      clearExpectingClose();
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'LOBBY_LIST_UPDATE') {
          if (!mountedRef.current) return;
          setLobbies(msg.payload);
          setLoading(false);
        }
      } catch (e) {
        console.error('[LobbyListPage] WS message parse error', e);
      }
    };

    ws.onerror = (err) => {
      console.error('[LobbyListPage] WS error', err);
    };

    ws.onclose = () => {
      console.log('[LobbyListPage] WS disconnected');
      if (!isExpectingClose()) {
        triggerConnectionLost();
      }
    };

    return () => {
      mountedRef.current = false;
      controller.abort();
      markExpectingClose();
      ws.close();
      wsRef.current = null;
    };
  }, [networkId]);

  const handleCreate = async () => {
    if (isGuest) {
      setLoginRequiredReason(LOGIN_REQUIRED_REASONS.CREATE_LOBBY);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/lobbies`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({playerName: networkName, playerId: networkId}),
      });
      if (!res.ok) throw new Error('Failed to create lobby');
      const data = await res.json();
      onEnterLobby(data.lobbyId);
    } catch (e) {
      setError(e.message);
    }
  };

  // К настройке поиска рейтингового матча — как кнопка главного меню: гостю
  // вместо нее модалка входа
  const handleRankedMatch = () => {
    if (isGuest) {
      setLoginRequiredReason(LOGIN_REQUIRED_REASONS.MATCH);
      return;
    }
    onRankedMatch?.();
  };

  const handleJoin = async (lobbyId, {isAiMatch = false} = {}) => {
    try {
      const res = await fetch(`${API_BASE}/lobbies/${lobbyId}/join`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({playerName: networkName, playerId: networkId}),
        // В промо-лобби с ИИ сервер сверяет playerId с JWT-кукой входа — на
        // дев-сервере (другой порт) без include кука не уходит. Остальные
        // лобби входят как раньше
        ...(isAiMatch ? {credentials: 'include'} : {}),
      });
      if (isGuest) {
        sendPageEvent('LobbyListPage', 'guest_spectate', res.ok ? 'ok' : 'refused',
          res.ok ? undefined : getGuestSpectateRefusalLevel(res.status));
      }
      // Гостю — понятное сообщение вместо служебного кода отказа (матч успел
      // закончиться, пока гость смотрел на список)
      if (isGuest && checkIfGuestJoinRefused(res.status)) {
        throw new Error(GUEST_SPECTATE_UNAVAILABLE_MESSAGE);
      }
      if (!res.ok) {
        // Отказы промо-лобби с ИИ — теми же текстами, что у кнопки «Битва с
        // AI» в главном меню (getAiLobbyJoinErrorText): нет действующего входа,
        // лобби закрыто, прочее. Пока тело не разобрано — по одному коду ответа
        let message = isAiMatch ? getAiLobbyJoinErrorText({status: res.status}) : 'Failed to join lobby';
        try {
          const errorData = await res.json();
          if (errorData.error === 'NOT_ENOUGH_FACTIONS') {
            onNotEnoughFactions?.(errorData.availableCount || 0);
            return;
          }
          // Мест нет (вошедшему бота не уступают) — объясняем модалкой, а
          // не строчкой ошибки над списком
          if (errorData.error === 'LOBBY_IS_FULL') {
            onLobbyIsFull?.(errorData.maxPlayers);
            return;
          }
          // «Уже в другом матче» у ИИ-лобби — тем же текстом, что и у
          // остальных: он подсказывает, как вернуться в свой матч
          if (isAiMatch && errorData.error !== ALREADY_IN_ANOTHER_LOBBY_ERROR) {
            message = getAiLobbyJoinErrorText(resolveInvitedLobbyJoinOutcome(res, errorData));
          } else if (errorData.error === 'RANK_MATCH_JOIN_NOT_ALLOWED') {
            message = RANK_MATCH_JOIN_NOT_ALLOWED_MESSAGE;
          } else if (errorData.error === 'RANK_MATCH_PHASE_STARTED') {
            message = RANK_MATCH_PHASE_STARTED_MESSAGE;
          } else if (errorData.error === ALREADY_IN_ANOTHER_LOBBY_ERROR) {
            message = 'Вы уже участвуете в матче!\nДля автоматического входа в него перезагрузите страницу.';
          } else if (errorData.error) {
            message = errorData.error;
          }
        } catch {
          // Ignore parse errors and use the fallback message.
        }
        throw new Error(message);
      }
      const data = await res.json();
      // Полное ИИ-лобби (или уже начавшиеся баны) сервер не отвергает, а
      // пускает зрителем (joinedAsSpectator) — это тоже вход в лобби. Модалку
      // «все места заняты» экран лобби показывает сам по составу лобби (см.
      // checkIfAiLobbySpectatorNoticeDue), как и при входе из главного меню
      onEnterLobby(data.lobbyId);
    } catch (e) {
      setError(e.message);
    }
  };

  // Сыгранный матч ('finished') доживает в памяти сервера, пока пишутся
  // итоги, — ни в сбор, ни в идущие: войти в него уже нельзя
  const preparationLobbies = sortAiLobbiesFirst(lobbies.filter((l) => l.status === 'preparation'));
  const startedLobbies = sortAiLobbiesFirst(lobbies.filter((l) => l.status === 'game_started'));

  // Клик по лобби: гость входит зрителем только в идущий неприватный матч, на
  // лобби в сборе, приватное и промо-лобби с ИИ (там только с аккаунтом, даже
  // зрителем) — модалка входа
  const handleLobbyClick = (lobby) => {
    if (isGuest && !checkIfGuestCanSpectateLobby(lobby)) {
      setLoginRequiredReason(LOGIN_REQUIRED_REASONS.MATCH);
      return;
    }
    if (lobby.isPrivate) {
      setShowPrivateLobbyOverlay(true);
      return;
    }
    handleJoin(lobby.lobbyId, {isAiMatch: Boolean(lobby.isAiMatch)});
  };

  const renderLobbyList = (list) =>
    list.map((lobby) => (
      <PageButton
        key={lobby.lobbyId}
        // Промо-лобби с ИИ выделено рамкой и свечением цвета алмазной лиги —
        // как кнопка «Битва с Claude Opus» в главном меню
        className={lobby.isAiMatch ? 'lobby-item lobby-item_ai' : 'lobby-item'}
        onClick={() => handleLobbyClick(lobby)}
        pageId="LobbyListPage"
        buttonId={getLobbyItemButtonId(lobby)}
        isGuest={isGuest}
      >
        <LobbyListTypeIcons lobby={lobby} isViewerAdmin={isViewerAdmin} isAdmin={isAdmin}/>
        {/* У ИИ-лобби «AI» показывает иконка слева — в названии его нет */}
        <span className="lobby-item-name">{lobby.isAiMatch ? AI_MATCH_SHORT_TITLE : `Лобби ${lobby.lobbyName}`}</span>
        <div className="lobby-item-count">
          <span>{lobby.playerCount ?? lobby.players.length}</span>
          <PlayerIcon
            className="lobby-item-count-icon"
            color="var(--text-primary-color)"
            isSmall
          />
        </div>
      </PageButton>
    ));

  // Модалки — общие для обеих раскладок и на одном месте в дереве: раскладка
  // меняется при повороте планшета через 1024 px, а открытая модалка (форма
  // входа гостя с введенной почтой) не должна монтироваться заново
  const modals = (
    <>
      {showPrivateLobbyOverlay && (
        <PrivateLobbyOverlay
          onShop={onShop}
          onBack={() => setShowPrivateLobbyOverlay(false)}
        />
      )}

      {isGuest && loginRequiredReason && (
        <LoginRequiredModal
          reason={loginRequiredReason}
          onClose={() => setLoginRequiredReason(null)}
          onDevLogin={onDevLogin}
          onEmailAuthSuccess={onEmailAuthSuccess}
        />
      )}
    </>
  );

  const renderWithModals = (layout) => (
    <>
      {layout}
      {modals}
    </>
  );

  if (isDesktopLayout) {
    return renderWithModals(
      <LobbyListDesktop
        preparationLobbies={preparationLobbies}
        startedLobbies={startedLobbies}
        isLoading={loading}
        error={error}
        infoText={LOBBY_LIST_INFO_TEXT}
        isGuest={isGuest}
        isViewerAdmin={isViewerAdmin}
        isAdmin={Boolean(isAdmin)}
        onLobbyClick={handleLobbyClick}
        onCreate={handleCreate}
        onRankedMatch={handleRankedMatch}
        onBack={onBack}
      />
    );
  }

  // Название — «Список лобби»: здесь и обычные, и рейтинговые лобби
  return renderWithModals(
    <>
      <FloatingBackButton onBack={onBack} pageId={'LobbyListPage'}/>

      <h1 className="lobby-list-title">Список лобби</h1>
      {loading && <p>Загрузка...</p>}
      {error && <p className="lobby-error">{error}</p>}

      <div className="lobby-list-info">
        <img className="lobby-list-info-icon" src="/img/icons/info.png" alt="" aria-hidden="true"/>
        <p className="lobby-list-info-text">{LOBBY_LIST_INFO_TEXT}</p>
      </div>

      <div className="lobby-list-container">
        <h2 className="lobby-section-title">Сбор игроков</h2>
        {/* Создание своего лобби — первым элементом списка лобби в сборе:
            рядом с тем, куда можно войти, а не под всеми списками */}
        <PageButton
          className="lobby-create-item"
          onClick={handleCreate}
          pageId="LobbyListPage"
          buttonId="create_lobby"
          isGuest={isGuest}
        >
          <span className="lobby-create-item_plus" aria-hidden="true">+</span>{' '}
          <span className="lobby-create-item_text">Создать обычное лобби</span>
        </PageButton>
        {preparationLobbies.length === 0 && !loading && (
          <p className="lobby-list_no-items">Нет лобби в сборе</p>
        )}
        {renderLobbyList(preparationLobbies)}
      </div>

      <div className="lobby-list-container">
        <h2 className="lobby-section-title">Начатые матчи</h2>
        {startedLobbies.length === 0 && !loading && (
          <p className="lobby-list_no-items">Нет начатых матчей</p>
        )}
        {renderLobbyList(startedLobbies)}
      </div>

      <div className="main-menu-buttons">
        <PageButton
          className="main-menu-button" onClick={onBack} pageId="LobbyListPage" buttonId="back" isGuest={isGuest}
        >Назад</PageButton>
      </div>
    </>
  );
}

export default LobbyListPage;
